/**
 * Decisions behind the install promotion (MEDA-1187): which way this surface can install the app,
 * the steps for a browser with no install prompt, whether the page already runs as the installed
 * app, and whether a dismissal still holds.
 */

/** How the promotion helps on this surface. */
export type InstallRoute = 'prompt' | 'open-in-browser' | 'manual';

/** What the promotion knows about where it runs. */
export interface InstallSurface {
  /** The page runs as the installed app. */
  standalone: boolean;
  /** The page is a Teams tab, in the browser or the desktop client. */
  inTeams: boolean;
  /** The browser handed over its install prompt (`beforeinstallprompt`). */
  promptReady: boolean;
  /** The browser has an install prompt at all (Chromium: Chrome, Edge, Opera and the like). */
  nativePromptSupported: boolean;
}

/** How long a dismissal hides the promotion. Decided with the user on 2026-10-27. */
export const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

/** Where a dismissal is remembered, per browser. */
export const DISMISS_STORAGE_KEY = 'ct-install-promotion-dismissed-at';

/**
 * The route for this surface, or null when there is nothing honest to offer. A Teams tab is a
 * frame or a webview that cannot install the app itself, so it hands over to the browser. A
 * Chromium tab that has not handed over its prompt is most likely installed already, or not yet
 * eligible, so it shows nothing rather than steps that may not apply.
 */
export function installRoute(surface: InstallSurface): InstallRoute | null {
  if (surface.standalone) return null;
  if (surface.inTeams) return 'open-in-browser';
  if (surface.promptReady) return 'prompt';
  return surface.nativePromptSupported ? null : 'manual';
}

/**
 * Steps a browser without an install prompt can follow, or null for a browser the promotion cannot
 * vouch for; `touchPoints` tells an iPad from a Mac. Every iPhone and iPad browser can add a page to
 * the home screen from its Share menu, which Edge, Chrome and Firefox keep in the browser menu;
 * Firefox installs apps on Android only.
 */
export function manualInstallHint(
  userAgent: string,
  touchPoints: number,
): string | null {
  if (isAppleMobile(userAgent, touchPoints))
    return /EdgiOS|CriOS|FxiOS/.test(userAgent)
      ? 'Open the browser menu, tap Share, then Add to Home Screen.'
      : 'Tap Share, then Add to Home Screen.';
  if (/Android/.test(userAgent) && /Firefox/.test(userAgent))
    return 'In Firefox, open the menu, then tap Add app to Home screen.';
  if (/Firefox/.test(userAgent))
    return 'Firefox cannot install apps. Open CT Agent in Edge or Chrome to install it.';
  if (/Safari/.test(userAgent) && !/Chrome|Chromium|Edg|OPR/.test(userAgent))
    return 'In Safari, choose File, then Add to Dock.';
  return null;
}

/** Whether the device is an iPhone or iPad; an iPad reports a Mac but has touch points. */
export function isAppleMobile(userAgent: string, touchPoints: number): boolean {
  return (
    /iPhone|iPad|iPod/.test(userAgent) ||
    (/Macintosh/.test(userAgent) && touchPoints > 1)
  );
}

/** Whether the page runs as the installed app: a standalone display mode, or an iOS home-screen app. */
export function isStandaloneDisplay(
  matches: (query: string) => boolean,
  iosStandalone: boolean | undefined,
): boolean {
  return (
    iosStandalone === true ||
    ['standalone', 'minimal-ui', 'fullscreen'].some((mode) =>
      matches(`(display-mode: ${mode})`),
    )
  );
}

/** Whether a dismissal stored as epoch milliseconds still hides the promotion at `now`. */
export function dismissedRecently(stored: string | null, now: number): boolean {
  const dismissedAt = Number(stored);
  if (!stored || !Number.isFinite(dismissedAt)) return false;
  return now - dismissedAt < DISMISS_COOLDOWN_MS;
}
